import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { ChevronDown, Menu, X } from "lucide-react";
import UserMenu from "@/components/site/UserMenu";
import { useSettings } from "@/lib/api";
import { StatusChip } from "@/night/ui";
import "@/night/night.css";

const MAIN = [["/", "الرئيسية", "home"], ["/departments", "الأقسام", "departments"], ["/rules", "القوانين", "rules"], ["/cars", "السيارات", "cars"], ["/news", "الأخبار", "news"]];
const MORE = [["/gangs", "باكيجات العصابات", "gangs"], ["/packages", "الباكيجات", "packages"], ["/businesses", "البزنس", "businesses"], ["/leaderboard", "الأكثر لعباً", "leaderboard"], ["/staff", "الطاقم", "staff"], ["/contact", "التواصل", "contact"]];
export const ALL = [...MAIN, ...MORE];

const cls = ({ isActive }) => `relative px-3 py-2 text-[15px] font-bold transition-colors ${isActive ? "text-white after:absolute after:inset-x-3 after:-bottom-0.5 after:h-0.5 after:bg-[var(--n-cyan)]" : "text-slate-400 hover:text-white"}`;

function More() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const active = MORE.some(([to]) => pathname.startsWith(to));
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button className={`flex items-center gap-1 px-3 py-2 text-[15px] font-bold hover:text-white ${active ? "text-[var(--n-cyan)]" : "text-slate-400"}`} data-testid="nav-more-btn">المزيد <ChevronDown size={14} /></button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} className="absolute right-0 top-full w-56 border border-[var(--n-line)] bg-[#060a12]/95 p-2 backdrop-blur-xl" data-testid="nav-more-menu">
            {MORE.map(([to, l, id]) => <NavLink key={to} to={to} className="block px-3 py-2.5 text-sm text-slate-300 transition-colors hover:bg-[var(--n-cyan)]/10 hover:text-white" data-testid={`nav-${id}`}>{l}</NavLink>)}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Navbar() {
  const [open, setOpen] = useState(false);
  const [solid, setSolid] = useState(false);
  const { data: s } = useSettings();
  useEffect(() => { const f = () => setSolid(window.scrollY > 40); f(); window.addEventListener("scroll", f); return () => window.removeEventListener("scroll", f); }, []);
  return (
    <header className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${solid || open ? "border-b border-[var(--n-line)] bg-[#030509]/85 backdrop-blur-xl" : "bg-gradient-to-b from-black/70 to-transparent"}`} data-testid="navbar">
      <div className="mx-auto flex max-w-[1400px] items-center gap-6 px-5 py-4 sm:px-8">
        <Link to="/" className="flex shrink-0 items-center gap-3" data-testid="nav-logo">
          <img src="/site/logo.png" alt="SORA RP" className="h-12 w-12 object-contain drop-shadow-[0_0_16px_rgba(0,225,255,0.6)]" />
          <span className="f-lat text-2xl leading-none tracking-wide" dir="ltr">SORA<span className="text-[var(--n-cyan)]">.RP</span></span>
        </Link>
        <nav className="hidden items-center lg:flex">
          {MAIN.map(([to, l, id]) => <NavLink key={to} to={to} end={to === "/"} className={cls} data-testid={`nav-${id}`}>{l}</NavLink>)}
          <More />
        </nav>
        <div className="ms-auto flex items-center gap-3">
          <div className="hidden md:block"><StatusChip /></div>
          <UserMenu />
          <Link to="/apply" className="btn-ice !hidden !py-2.5 sm:!inline-flex" data-testid="nav-apply-btn">قدّم الحين</Link>
          <a href={s?.discord_invite} target="_blank" rel="noreferrer" className="sr-only" data-testid="nav-discord-btn">ديسكورد</a>
          <button onClick={() => setOpen(!open)} className="border border-[var(--n-line-2)] p-2 lg:hidden" aria-label="القائمة" data-testid="nav-menu-toggle">{open ? <X size={18} /> : <Menu size={18} />}</button>
        </div>
      </div>
      {open && (
        <nav className="grid grid-cols-2 gap-1 border-t border-[var(--n-line)] px-5 pb-5 pt-3 lg:hidden" data-testid="nav-mobile">
          {ALL.map(([to, l, id]) => <NavLink key={to} to={to} end={to === "/"} onClick={() => setOpen(false)} className="px-3 py-2.5 text-sm text-slate-300 hover:text-white" data-testid={`nav-m-${id}`}>{l}</NavLink>)}
          <Link to="/apply" onClick={() => setOpen(false)} className="btn-ice col-span-2 mt-2" data-testid="nav-m-apply">قدّم الحين</Link>
        </nav>
      )}
    </header>
  );
}

function Footer() {
  const { data: s } = useSettings();
  return (
    <footer className="relative mt-32 overflow-hidden border-t border-[var(--n-line)] bg-[#020307]" data-testid="footer">
      <div className="siren" />
      <div className="mx-auto grid max-w-[1400px] gap-12 px-5 pb-10 pt-20 sm:px-8 lg:grid-cols-[1.2fr_2fr]">
        <div>
          <p className="f-ar text-3xl leading-[1.5]">سورا وطن بلا حدود...<br /><span className="glow-text">فكن أنت بلا حدود</span></p>
          <a href={s?.discord_invite} target="_blank" rel="noreferrer" className="btn-ice mt-8" data-testid="footer-discord-btn">انضم للديسكورد</a>
        </div>
        <div className="grid grid-cols-2 gap-x-8 gap-y-4 sm:grid-cols-3">
          {ALL.map(([to, l, id]) => <Link key={to} to={to} className="text-[15px] text-slate-400 transition-colors hover:text-[var(--n-cyan)]" data-testid={`footer-${id}`}>{l}</Link>)}
          <Link to="/apply" className="text-[15px] text-slate-400 hover:text-[var(--n-cyan)]" data-testid="footer-apply">التقديم</Link>
        </div>
      </div>
      <div aria-hidden className="f-lat outline-soft select-none whitespace-nowrap text-center text-[19vw] leading-[0.8]" dir="ltr">SORA RP</div>
      <div className="border-t border-white/5 py-5 text-center f-mono text-[11px] tracking-[0.3em] text-slate-600" dir="ltr">© {new Date().getFullYear()} SORA RP · LOS SANTOS</div>
    </footer>
  );
}

function Cursor() {
  const ref = useRef(null);
  useEffect(() => {
    const move = (e) => {
      if (!ref.current) return;
      ref.current.style.transform = `translate(${e.clientX}px, ${e.clientY}px)`;
      ref.current.classList.toggle("big", !!e.target.closest?.("a,button"));
    };
    window.addEventListener("mousemove", move);
    return () => window.removeEventListener("mousemove", move);
  }, []);
  return <div ref={ref} className="cursor-dot" aria-hidden />;
}

export default function NightLayout() {
  const { pathname, search } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  useEffect(() => { if (new URLSearchParams(search).get("login") === "failed") toast.error("ما قدرنا نسجّل دخولك بالديسكورد، جرّب مرة ثانية"); }, [search]);
  return (
    <div className="night" dir="rtl" data-testid="site-root">
      <Cursor />
      <Navbar />
      <AnimatePresence mode="wait">
        <motion.main key={pathname} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }} className="min-h-[70vh]">
          <Outlet />
        </motion.main>
      </AnimatePresence>
      <Footer />
    </div>
  );
}
